import React, {useEffect, useState} from 'react';
import {Row, Col, Form, Button, InputGroup} from "react-bootstrap";
import ProductItem from "./ProductItem";
import { FiSearch, FiSliders, FiRefreshCw, FiCheckCircle, FiFilter } from 'react-icons/fi';

const ProductList = ({
    products,
    page,
    setPage,
    totalCount,
    limit,
    setShowFilters,
    search,
    setSearch,
    sortBy,
    sortOrder,
    onSortChange,
    onSearchSubmit,
    isSearching = false,
    isLoading = false,
    hasLoadedOnce = false,
    activeFilters = {},
    hasActiveFilters = false,
    onResetFilters
}) => {
    console.log('ProductList debug:', { productsLength: products.length, totalCount, page, limit });

    const [localSearch, setLocalSearch] = useState(search || '');

    useEffect(() => {
        setLocalSearch(search || '');
    }, [search]);

    const handleSearchClick = () => {
        const value = (localSearch || '').trim();
        if (onSearchSubmit) {
            onSearchSubmit(value);
        } else if (setSearch) {
            setSearch(value);
        }
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearchClick();
        }
    };

    const handleClearSearch = () => {
        setLocalSearch('');
        if (onSearchSubmit) {
            onSearchSubmit('');
        } else if (setSearch) {
            setSearch('');
        }
    };

    return (
        <div className="product-list shop-container-fluid">
            <div className="product-toolbar-card">
                <div className="toolbar-heading d-flex flex-wrap justify-content-between align-items-start gap-2">
                    <div>
                        <p className="eyebrow">Поиск и фильтры</p>
                        <h3 className="toolbar-title">Быстрый подбор товаров</h3>
                        <div className="toolbar-subtitle">
                            Найдено {totalCount || 0} позиций • на странице сейчас {products.length}
                        </div>
                    </div>
                    <div className="toolbar-meta d-flex flex-wrap gap-2">
                        <span className="soft-badge">
                            <FiCheckCircle className="me-1" /> {products.length} на странице
                        </span>
                        {hasActiveFilters && (
                            <span className="soft-badge success">
                                <FiFilter className="me-1" /> Фильтры активны
                            </span>
                        )}
                        {isSearching && (
                            <span className="soft-badge muted">
                                Идёт поиск…
                            </span>
                        )}
                    </div>
                </div>

                <Row className="product-list-search-row g-3 align-items-center">
                    <Col xs={12} md={7} lg={6}>
                        <div className="search-block">
                            <Form.Control
                                type="text"
                                placeholder="Введите название, артикул или ключевое слово"
                                value={localSearch}
                                onChange={e => setLocalSearch(e.target.value)}
                                onKeyDown={handleSearchKeyDown}
                                className="search-full-input"
                            />
                            <div className="search-actions">
                                {localSearch && (
                                    <Button
                                        variant="outline-secondary"
                                        className="search-clear-btn"
                                        onClick={handleClearSearch}
                                        aria-label="Очистить поиск"
                                    >
                                        Очистить
                                    </Button>
                                )}
                                <Button
                                    variant="primary"
                                    className="search-submit-btn"
                                    onClick={handleSearchClick}
                                    aria-label="Найти товары"
                                >
                                    Найти
                                </Button>
                            </div>
                        </div>
                    </Col>
                    <Col xs={12} md={5} lg={3}>
                        <div className="field-label">Сортировка</div>
                        <Form.Select 
                            value={`${sortBy}-${sortOrder}`}
                            onChange={e => {
                                const [newSortBy, newSortOrder] = e.target.value.split('-');
                                onSortChange(newSortBy, newSortOrder);
                            }}
                        >
                            <option value="price-asc">Цена (по возрастанию)</option>
                            <option value="price-desc">Цена (по убыванию)</option>
                            <option value="quantity-asc">Остаток (по возрастанию)</option>
                            <option value="quantity-desc">Остаток (по убыванию)</option>
                            <option value="rating-asc">Рейтинг (по возрастанию)</option>
                            <option value="rating-desc">Рейтинг (по убыванию)</option>
                            <option value="createdAt-desc">Новинки</option>
                        </Form.Select>
                    </Col>
                    <Col xs={12} md={4} lg={3}>
                        <div className="field-label d-none d-lg-block">Фильтры</div>
                        <Button variant="outline-primary" className="w-100 filters-button" onClick={() => setShowFilters(true)}>
                            <FiSliders className="me-2" /> Настроить фильтры
                        </Button>
                    </Col>
                </Row>

                {(hasActiveFilters || search) && (
                    <div className="active-filters-bar">
                        {search && (
                            <span className="filter-chip">
                                <FiSearch /> Запрос: <strong>"{search}"</strong>
                            </span>
                        )}
                        {activeFilters.category && (
                            <span className="filter-chip">
                                <FiCheckCircle /> Категория: {activeFilters.category.name}
                            </span>
                        )}
                        {activeFilters.store && (
                            <span className="filter-chip">
                                <FiCheckCircle /> Магазин: {activeFilters.store.name}
                            </span>
                        )}
                        {Array.isArray(activeFilters.sizes) && activeFilters.sizes.length > 0 && (
                            <span className="filter-chip">
                                <FiCheckCircle /> Размеры: {activeFilters.sizes.join(', ')}
                            </span>
                        )}
                        {activeFilters.gender && (
                            <span className="filter-chip">
                                <FiCheckCircle /> Пол: {activeFilters.gender === 'мужской' ? 'Мужская' : activeFilters.gender === 'женский' ? 'Женская' : activeFilters.gender === 'детский' ? 'Детская' : activeFilters.gender}
                            </span>
                        )}
                        {(hasActiveFilters || search) && onResetFilters && (
                            <Button
                                variant="link"
                                size="sm"
                                className="chip-reset"
                                onClick={onResetFilters}
                            >
                                <FiRefreshCw className="me-1" /> Сбросить фильтры и поиск
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {isLoading || !hasLoadedOnce ? (
                <div className="product-list-empty-state product-list-loading-state mt-3">
                    <h4 className="product-list-loading-title">Загружаем товары…</h4>
                    <p>Пожалуйста, подождите, мы подбираем подходящие товары по выбранным параметрам.</p>
                </div>
            ) : (
                <>
                    {products.length === 0 ? (
                        <div className="product-list-empty-state mt-3">
                            <h4>Товары не найдены</h4>
                            {search ? (
                                <p>По запросу <strong>"{search}"</strong> ничего не найдено.</p>
                            ) : (
                                <p>По текущим фильтрам товары не найдены.</p>
                            )}
                            <ul>
                                <li>Попробуйте изменить запрос или удалить часть слов.</li>
                                <li>Сбросьте фильтры или выберите другие параметры.</li>
                                <li>Убедитесь, что выбран правильный раздел каталога.</li>
                            </ul>
                        </div>
                    ) : (
                        <Row className="g-3 g-md-4 mt-1">
                            {products.map(product =>
                                <Col xs={6} md={4} lg={3} key={product.id} className="d-flex">
                                    <ProductItem product={product}/>
                                </Col>
                            )}
                        </Row>
                    )}
                </>
            )}
        </div>
    );
};

export default ProductList;